/**
 * Voters — the "rows to fix" file.
 *
 * The rows that did not import, in the user's OWN columns (original headers, original cell values,
 * original order) plus one last column, "What to fix". Fix the cells, delete that column or not,
 * upload the file again: the headers are the ones that were already mapped, so the second import
 * maps itself. That round trip is the test that matters (`__tests__/rows-to-fix.test.ts`).
 *
 * Formula-injection guard: a cell that a spreadsheet would execute (`=`, `+`, `-`, `@`, tab, CR) is
 * prefixed with an apostrophe — EXCEPT a phone or number like `+2348031112233` or `-5`, which is
 * data the user typed and must survive the round trip intact.
 */

import Papa from 'papaparse';

export const WHAT_TO_FIX_HEADER = 'What to fix';

const NUMERIC_LIKE = /^[+-]?[\d\s()]+$/;

export const guardCell = (value: unknown): string => {
	const text = value === null || value === undefined ? '' : String(value);
	if (!text) return text;
	if (/^[=+\-@\t\r]/.test(text) && !NUMERIC_LIKE.test(text)) return `'${text}`;
	return text;
};

export interface IRowToFix {
	cells: readonly unknown[];
	whatToFix: string;
}

/** Drop a trailing "What to fix" column carried in from a previous rows-to-fix file. */
export const stripWhatToFix = (headers: readonly string[]): { headers: string[]; width: number } => {
	const index = headers.findIndex((h) => h.trim().toLowerCase() === WHAT_TO_FIX_HEADER.toLowerCase());
	const kept = index === -1 ? [...headers] : headers.slice(0, index);
	return { headers: kept, width: kept.length };
};

export const buildRowsToFixCsv = (headers: readonly string[], rows: readonly IRowToFix[]): string => {
	const { headers: kept, width } = stripWhatToFix(headers);
	const fields = [...kept, WHAT_TO_FIX_HEADER];
	const data = rows.map((row) => {
		const cells = Array.from({ length: width }, (_, i) => guardCell(row.cells[i]));
		return [...cells, guardCell(row.whatToFix)];
	});
	// BOM so Excel opens UTF-8 names (Ọlá, Ńnàmdí) correctly.
	return `\uFEFF${Papa.unparse({ fields, data })}`;
};

export const rowsToFixFileName = (sourceName: string): string => {
	const base = sourceName.replace(/\.[^.]+$/, '') || 'voters';
	return `${base}-rows-to-fix.csv`;
};
